/**
 * Player entity: a small sprite that moves tile-by-tile through the maze.
 *
 * Movement is grid-based with smooth tweening. Each completed move
 * triggers a health drain via the HealthSystem.
 *
 * Features:
 *  - Idle pulse animation
 *  - Movement sound (plays every BALL_SOUND_INTERVAL ms while moving,
 *    stops immediately when the player stops — even mid-playback)
 *  - Dust particle trail spawned behind the player during movement
 */

import Phaser from 'phaser';
import { TEX_PLAYER, TEX_DUST, TEX_GLOW, Direction, Events } from '../utils/constants.js';
import {
  TILE_SIZE,
  PLAYER_MOVE_DURATION,
  AUDIO_BALL_MOVING,
  BALL_SOUND_INTERVAL,
  AGMUTH_GHOST_ALPHA,
  AGMUTH_GHOST_GLOW_COLOR,
  AGMUTH_GHOST_GLOW_ALPHA,
  AGMUTH_GHOST_PARTICLE_FREQ,
} from '../config/game-config.js';
import MazeGenerator from '../systems/MazeGenerator.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class Player extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} gridCol - Starting grid column.
   * @param {number} gridRow - Starting grid row.
   * @param {number[][]} mazeGrid - Reference to the maze grid for collision checks.
   * @param {Phaser.Events.EventEmitter} emitter - Shared event bus.
   */
  constructor(scene, gridCol, gridRow, mazeGrid, emitter) {
    const worldX = gridCol * TILE_SIZE + TILE_SIZE / 2;
    const worldY = gridRow * TILE_SIZE + TILE_SIZE / 2;

    super(scene, worldX, worldY, TEX_PLAYER);

    this.scene = scene;
    this.emitter = emitter;
    this.mazeGrid = mazeGrid;

    /** Current logical grid position. */
    this.gridCol = gridCol;
    this.gridRow = gridRow;

    /** Whether a move tween is in progress. */
    this.isMoving = false;

    /** @private Target grid column during an active move tween, or null. */
    this._targetCol = null;
    /** @private Target grid row during an active move tween, or null. */
    this._targetRow = null;

    /** @private Last movement direction for directional dust trail. */
    this._lastDirection = null;

    // --- Movement sound state ---
    /** @private {Phaser.Sound.BaseSound|null} Current ball-moving sound instance. */
    this._movingSound = null;
    /** @private {Phaser.Time.TimerEvent|null} Repeating timer for movement sound. */
    this._movingSoundTimer = null;
    /** @private Tracks whether we were moving last frame (for stop detection). */
    this._wasMoving = false;

    // --- Move duration (can be modified externally, e.g. by poison effect) ---
    /** @type {number} Current move tween duration in ms. Defaults to PLAYER_MOVE_DURATION. */
    this.moveDuration = PLAYER_MOVE_DURATION;

    // --- Agmuth Ghost Mode ---
    /** @type {boolean} Whether ghost mode is currently active. */
    this._ghostMode = false;
    /** @private {Phaser.GameObjects.Particles.ParticleEmitter|null} Gold glow emitter for ghost mode. */
    this._ghostGlowEmitter = null;
    /** @private {Phaser.GameObjects.Graphics|null} Gold halo overlay for ghost mode. */
    this._ghostHalo = null;

    // --- Dust particle emitter ---
    /** @private {Phaser.GameObjects.Particles.ParticleEmitter|null} */
    this._dustEmitter = null;

    // Add to scene
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Render above hazard floor tiles (water/lava/poison are at depth 2)
    this.setDepth(5);

    // Shrink physics body slightly for smoother wall sliding
    const body = /** @type {Phaser.Physics.Arcade.Body} */ (this.body);
    body.setSize(TILE_SIZE * 0.6, TILE_SIZE * 0.6);
    body.setImmovable(false);

    // Subtle idle pulse animation
    this._createIdlePulse();

    // Set up dust particles
    this._createDustEmitter();
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Attempt to move in the given direction.
   * Only succeeds if the target cell is walkable and no tween is active.
   *
   * @param {string} direction - One of Direction enum values.
   * @returns {boolean} Whether the move was initiated.
   */
  move(direction) {
    if (this.isMoving) {
      return false;
    }

    const delta = Player._directionDelta(direction);
    if (!delta) {
      return false;
    }

    const targetCol = this.gridCol + delta.dx;
    const targetRow = this.gridRow + delta.dy;

    if (!MazeGenerator.isWalkable(this.mazeGrid, targetCol, targetRow)) {
      return false;
    }

    this.isMoving = true;
    this._targetCol = targetCol;
    this._targetRow = targetRow;

    // Store direction for directional dust trail
    this._lastDirection = direction;

    // Start movement sound if not already playing
    this._startMovementSound();

    // Enable dust particles in the opposite direction of movement
    if (this._dustEmitter) {
      this._updateDustDirection(direction);
      this._dustEmitter.start();
    }

    const targetX = targetCol * TILE_SIZE + TILE_SIZE / 2;
    const targetY = targetRow * TILE_SIZE + TILE_SIZE / 2;

    this.scene.tweens.add({
      targets: this,
      x: targetX,
      y: targetY,
      duration: this.moveDuration,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.gridCol = targetCol;
        this.gridRow = targetRow;
        this.isMoving = false;
        this._targetCol = null;
        this._targetRow = null;
        this.emitter.emit(Events.PLAYER_MOVED, this.gridCol, this.gridRow);
      },
    });

    return true;
  }

  /**
   * Called each frame by the scene to manage sound and particle state.
   * The scene's update() should call this.
   */
  updatePlayer() {
    // Detect when the player stops moving (no tween active, and
    // was moving previously OR no new move is queued this frame).
    // This is called AFTER _handleInput in GameScene, so if the
    // player is still pressing keys, isMoving may already be true
    // from a new move() call.
    if (!this.isMoving && this._wasMoving) {
      this._stopMovementSound();
      this._stopDust();
    }

    this._wasMoving = this.isMoving;
  }

  /**
   * Teleport the player to a grid cell without tweening.
   *
   * @param {number} col
   * @param {number} row
   */
  setGridPosition(col, row) {
    this.gridCol = col;
    this.gridRow = row;
    this.x = col * TILE_SIZE + TILE_SIZE / 2;
    this.y = row * TILE_SIZE + TILE_SIZE / 2;
  }

  /**
   * Get the current grid position.
   *
   * @returns {{ col: number, row: number }}
   */
  getGridPosition() {
    return { col: this.gridCol, row: this.gridRow };
  }

  /**
   * Get the destination cell during an active move tween.
   * Returns null if the player is not currently moving.
   *
   * @returns {{ col: number, row: number }|null}
   */
  getDestination() {
    if (this.isMoving && this._targetCol !== null && this._targetRow !== null) {
      return { col: this._targetCol, row: this._targetRow };
    }
    return null;
  }

  /**
   * Clean up resources when the player is destroyed.
   * @param {boolean} [fromScene] - Whether the scene is shutting down.
   */
  destroy(fromScene) {
    this._stopMovementSound();
    this._stopDust();

    // Clean up ghost mode if active
    if (this._ghostMode) {
      this._ghostMode = false;
      if (this._ghostHaloTween) {
        this._ghostHaloTween.stop();
        this._ghostHaloTween = null;
      }
      if (this._ghostHalo) {
        this._ghostHalo.destroy();
        this._ghostHalo = null;
      }
      if (this._ghostGlowEmitter) {
        this._ghostGlowEmitter.destroy();
        this._ghostGlowEmitter = null;
      }
    }

    if (this._idlePulseTween) {
      this._idlePulseTween.stop();
      this._idlePulseTween = null;
    }

    if (this._dustEmitter) {
      this._dustEmitter.destroy();
      this._dustEmitter = null;
    }

    super.destroy(fromScene);
  }

  // ---------------------------------------------------------------
  // Movement Sound
  // ---------------------------------------------------------------

  /**
   * Start the repeating movement sound if not already active.
   * Plays the ball-moving sound immediately and then every
   * BALL_SOUND_INTERVAL ms until _stopMovementSound() is called.
   * @private
   */
  _startMovementSound() {
    // Already playing — nothing to do
    if (this._movingSoundTimer) {
      return;
    }

    // Play immediately
    this._playMovingSoundOnce();

    // Set up repeating timer
    this._movingSoundTimer = this.scene.time.addEvent({
      delay: BALL_SOUND_INTERVAL,
      loop: true,
      callback: () => {
        this._playMovingSoundOnce();
      },
    });
  }

  /**
   * Play the ball-moving sound once, stopping any previous instance.
   * @private
   */
  _playMovingSoundOnce() {
    if (!this.scene || !this.scene.sound) return;

    // Destroy previous instance to prevent WebAudioSound object leak
    if (this._movingSound) {
      if (this._movingSound.isPlaying) {
        this._movingSound.stop();
      }
      this._movingSound.destroy();
      this._movingSound = null;
    }

    this._movingSound = this.scene.sound.add(AUDIO_BALL_MOVING, {
      volume: getSfxVol(0.35),
    });
    this._movingSound.play();
  }

  /**
   * Immediately stop the movement sound and cancel the repeating timer.
   * @private
   */
  _stopMovementSound() {
    // Cancel the repeating timer
    if (this._movingSoundTimer) {
      this._movingSoundTimer.destroy();
      this._movingSoundTimer = null;
    }

    // Stop any currently playing sound instance immediately
    if (this._movingSound) {
      if (this._movingSound.isPlaying) {
        this._movingSound.stop();
      }
      this._movingSound.destroy();
      this._movingSound = null;
    }
  }

  // ---------------------------------------------------------------
  // Dust Particle Trail
  // ---------------------------------------------------------------

  /**
   * Create the particle emitter for the dust trail.
   * Uses the TEX_DUST procedural texture. Particles spawn at the
   * player's position, drift in the opposite direction of movement,
   * shrink, and fade.
   * @private
   */
  _createDustEmitter() {
    if (!this.scene || !this.scene.add) return;

    this._dustEmitter = this.scene.add.particles(0, 0, TEX_DUST, {
      follow: this,
      followOffset: { x: 0, y: 0 },
      lifespan: { min: 250, max: 500 },
      speed: { min: 10, max: 35 },
      angle: { min: 160, max: 200 },  // Default: trail left (will be updated per-move)
      scale: { start: 0.9, end: 0 },
      alpha: { start: 0.6, end: 0 },
      frequency: 25,
      quantity: 2,
      emitting: false,  // Start paused — only emit while moving
    });

    // Dust should appear below the player but above the floor
    this._dustEmitter.setDepth(this.depth - 1);
  }

  /**
   * Update the dust emitter angle to trail behind the movement direction.
   * Particles emit in the opposite direction of travel with a spread.
   * @private
   * @param {string} direction - One of Direction enum values.
   */
  _updateDustDirection(direction) {
    if (!this._dustEmitter) return;

    const spread = 25; // degrees of spread on each side
    let baseAngle;

    switch (direction) {
      case Direction.UP:    baseAngle = 90;  break;  // Trail downward
      case Direction.DOWN:  baseAngle = 270; break;  // Trail upward
      case Direction.LEFT:  baseAngle = 0;   break;  // Trail rightward
      case Direction.RIGHT: baseAngle = 180; break;  // Trail leftward
      default: return;
    }

    // Update the EmitterOp's min/max range directly (method 6: start=min, end=max)
    const angleOp = this._dustEmitter.ops.angle;
    angleOp.start = baseAngle - spread;
    angleOp.end = baseAngle + spread;
  }

  /**
   * Stop emitting dust particles (existing ones will finish fading).
   * @private
   */
  _stopDust() {
    if (this._dustEmitter) {
      this._dustEmitter.stop();
    }
  }

  // ---------------------------------------------------------------
  // Life Lost Effect
  // ---------------------------------------------------------------

  /**
   * Play a red glow + pulse effect on the player ball when a life is lost.
   * Emits red-tinted glow particles briefly and pulses the ball scale.
   */
  playLifeLostEffect() {
    if (!this.scene) return;

    // Stop the idle pulse so it doesn't fight with the life-lost scale tweens
    this._stopIdlePulse();

    // --- Red glow particles burst ---
    const redGlow = this.scene.add.particles(this.x, this.y, TEX_GLOW, {
      follow: this,
      lifespan: { min: 300, max: 600 },
      speed: { min: 15, max: 50 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.6, end: 0 },
      alpha: { start: 0.8, end: 0 },
      frequency: 30,
      quantity: 4,
      tint: 0xff3333,
      emitting: true,
    });
    redGlow.setDepth(this.depth - 1);

    // Stop emitting after 600ms, let remaining particles fade out
    this.scene.time.delayedCall(600, () => {
      if (redGlow && redGlow.active) {
        redGlow.stop();
      }
    });

    // Destroy the emitter after all particles have finished (lifespan max = 600ms)
    this.scene.time.delayedCall(1300, () => {
      if (redGlow && redGlow.active) {
        redGlow.destroy();
      }
    });

    // --- Red pulsing scale effect on the player ball ---
    // Double-pulse: quick scale up → down → up → down (like the heart animation)
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.4,
      scaleY: 1.4,
      duration: 120,
      yoyo: true,
      ease: 'Quad.easeOut',
      onComplete: () => {
        if (!this.scene) return;
        // Second (smaller) pulse
        this.scene.tweens.add({
          targets: this,
          scaleX: 1.25,
          scaleY: 1.25,
          duration: 100,
          yoyo: true,
          ease: 'Quad.easeOut',
          onComplete: () => {
            // Restart the idle pulse now that the effect is done
            if (this.scene) this._restartIdlePulse();
          },
        });
      },
    });

    // --- Brief red tint flash via a red overlay circle ---
    const redFlash = this.scene.add.circle(this.x, this.y, TILE_SIZE * 0.45, 0xff3333, 0.5);
    redFlash.setDepth(this.depth + 2);

    // Follow the player in case they're moving
    const followUpdate = () => {
      if (this.active && redFlash.active) {
        redFlash.setPosition(this.x, this.y);
      }
    };
    this.scene.events.on('update', followUpdate);

    // Fade out the red flash
    this.scene.tweens.add({
      targets: redFlash,
      alpha: 0,
      duration: 500,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.scene?.events?.off('update', followUpdate);
        if (redFlash.active) redFlash.destroy();
      },
    });
  }

  // ---------------------------------------------------------------
  // Agmuth Ghost Mode
  // ---------------------------------------------------------------

  /**
   * Whether ghost mode is currently active.
   * @returns {boolean}
   */
  get isGhostMode() {
    return this._ghostMode;
  }

  /**
   * Activate ghost mode: make the soul semi-translucent and emit
   * a golden glow aura with particles.
   */
  startGhostMode() {
    if (this._ghostMode) return;
    this._ghostMode = true;

    // Semi-translucent soul
    this.setAlpha(AGMUTH_GHOST_ALPHA);

    // Create a golden halo circle that follows the player
    this._ghostHalo = this.scene.add.graphics();
    this._ghostHalo.setDepth(this.depth - 1);
    this._updateGhostHalo();

    // Pulsing halo glow
    this._ghostHaloTween = this.scene.tweens.add({
      targets: { val: 0 },
      val: 1,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._updateGhostHalo();
      },
    });

    // Gold glow particle emitter following the player
    try {
      this._ghostGlowEmitter = this.scene.add.particles(0, 0, TEX_GLOW, {
        follow: this,
        lifespan: { min: 300, max: 600 },
        speed: { min: 8, max: 25 },
        angle: { min: 0, max: 360 },
        scale: { start: 0.4, end: 0 },
        alpha: { start: AGMUTH_GHOST_GLOW_ALPHA, end: 0 },
        frequency: AGMUTH_GHOST_PARTICLE_FREQ,
        quantity: 2,
        tint: AGMUTH_GHOST_GLOW_COLOR,
        emitting: true,
      });
      this._ghostGlowEmitter.setDepth(this.depth + 1);
    } catch (_err) {
      // Graceful fallback if particles fail
    }
  }

  /**
   * Deactivate ghost mode: restore the soul to full opacity and
   * remove the golden glow effects.
   */
  stopGhostMode() {
    if (!this._ghostMode) return;
    this._ghostMode = false;

    // Restore full opacity (IMPORTANT: player sphere must NEVER lose opacity permanently)
    this.setAlpha(1);

    // Stop and destroy the halo
    if (this._ghostHaloTween) {
      this._ghostHaloTween.stop();
      this._ghostHaloTween = null;
    }
    if (this._ghostHalo) {
      this._ghostHalo.destroy();
      this._ghostHalo = null;
    }

    // Stop and destroy the gold glow emitter
    if (this._ghostGlowEmitter) {
      const emitter = this._ghostGlowEmitter;
      this._ghostGlowEmitter = null;       // Null ref immediately to prevent orphaning
      emitter.stop();
      // Let remaining particles fade out, then destroy
      if (this.scene) {
        this.scene.time.delayedCall(700, () => {
          if (emitter && emitter.active) {
            emitter.destroy();
          }
        });
      } else {
        emitter.destroy();
      }
    }
  }

  /**
   * Update the ghost halo position and appearance.
   * Called each frame from the scene's update loop when ghost mode is active.
   */
  updateGhostHalo() {
    if (this._ghostHalo && this._ghostMode) {
      this._updateGhostHalo();
    }
  }

  /** @private Redraw the ghost halo at the player's current position. */
  _updateGhostHalo() {
    if (!this._ghostHalo) return;
    this._ghostHalo.clear();

    // Outer soft glow
    this._ghostHalo.fillStyle(AGMUTH_GHOST_GLOW_COLOR, 0.12);
    this._ghostHalo.fillCircle(this.x, this.y, TILE_SIZE * 0.9);

    // Inner brighter ring
    this._ghostHalo.fillStyle(AGMUTH_GHOST_GLOW_COLOR, 0.25);
    this._ghostHalo.fillCircle(this.x, this.y, TILE_SIZE * 0.55);
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Map a Direction enum value to a grid delta.
   * @private
   * @param {string} dir
   * @returns {{ dx: number, dy: number }|null}
   */
  static _directionDelta(dir) {
    switch (dir) {
      case Direction.UP:    return { dx: 0, dy: -1 };
      case Direction.DOWN:  return { dx: 0, dy: 1 };
      case Direction.LEFT:  return { dx: -1, dy: 0 };
      case Direction.RIGHT: return { dx: 1, dy: 0 };
      default: return null;
    }
  }

  /**
   * Add a subtle scale pulse so the player feels alive.
   * @private
   */
  _createIdlePulse() {
    this._idlePulseTween = this.scene.tweens.add({
      targets: this,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Stop the idle pulse tween and reset scale to 1.
   * @private
   */
  _stopIdlePulse() {
    if (this._idlePulseTween) {
      this._idlePulseTween.stop();
      this._idlePulseTween = null;
    }
    this.setScale(1);
  }

  /**
   * Restart the idle pulse after it was stopped (e.g. after life-lost effect).
   * @private
   */
  _restartIdlePulse() {
    if (this._idlePulseTween) {
      this._idlePulseTween.stop();
    }
    this._createIdlePulse();
  }
}
